import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const UserContext = createContext();

export function UserProvider({ children }) {
  const savedUser  = JSON.parse(localStorage.getItem('user') || '{}');
  const token      = localStorage.getItem('access_token') || '';

  // use env base URL, keep the same API_BASE variable name/pattern
  const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  const API_BASE = `${API_ROOT}/api/profile`;

  // grab whichever ID field your backend returned at login
  const initialId =
    savedUser.user_id ||
    savedUser.id      ||
    savedUser._id     ||
    '';

  const [user, setUser] = useState({
    id:            initialId,
    user_name:     savedUser.user_name     || '',
    email:         savedUser.email         || '',
    phone:         savedUser.phone         || '',
    profile_image: savedUser.profile_image || '/assets/img/admin profile.jpg',
    version:       Date.now(),
  });

  const onLoginPage =
    typeof window !== 'undefined' &&
    window.location.pathname.toLowerCase().startsWith('/login');

  const fetchProfile = useCallback(async () => {
    if (onLoginPage) return; // don’t fetch while on login page

    // re-read the latest token at call time (in case it was just set after login)
    const liveToken = localStorage.getItem('access_token') || token;
    if (!liveToken || !user.id) return;

    try {
      const res = await fetch(`${API_BASE}/${user.id}`, {
        headers: {
          Authorization: `Bearer ${liveToken}`,
          Accept:        'application/json',
        },
      });
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const json = await res.json();
      const d    = json.data || json;

      setUser(prev => ({
        id:            d._id  || prev.id,
        user_name:     d.user_name     || prev.user_name,
        email:         d.email         || prev.email,
        phone:         d.phone         || prev.phone,
        profile_image: d.profile_image
          ? `${API_BASE}/images/${d.profile_image}`
          : prev.profile_image,
        version:       Date.now(),
      }));
    } catch (err) {
      console.error('fetchProfile error', err);
    }
  }, [token, user.id, API_BASE, onLoginPage]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <UserContext.Provider value={{ user, setUser, fetchProfile }}>
      {children}
    </UserContext.Provider>
  );
}

// ─── CUSTOM HOOK ───────────────────────────────────────────────────────
// Usage: const { user, setUser, fetchProfile } = useUser();
export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used inside a <UserProvider>');
  return ctx;
}
